import { Text, ActionIcon, Tabs, CopyButton } from '@mantine/core';
import { IconCheck, IconCopy, IconX } from '@tabler/icons-react';
import type { NetworkLog } from '~/common/vm/protocol';
import { Badge } from '@mantine/core';
import { match } from 'ts-pattern';

export function NetworkDetailsView({ log, onBack }: { log: NetworkLog; onBack: () => void }) {
  return (
    <div className="flex h-full flex-col border-t border-neutral-200 dark:border-neutral-700">
      <div className="flex flex-shrink-0 items-center justify-between border-b border-neutral-200 p-1.5 dark:border-neutral-700">
        {/* Header */}
        <div className="flex items-center gap-2">
          <ActionIcon
            onClick={onBack}
            size="sm"
            variant="subtle"
            color="gray"
          >
            <IconX
              size="0.875rem"
              className="text-[var(--mantine-color-text)]"
            />
          </ActionIcon>
          <Text
            className="font-medium"
            size="sm"
          >
            Network Request Details
          </Text>
        </div>

        <div className="flex items-center gap-2">
          <Text
            c="dimmed"
            size="sm"
          >
            {new Date(log.startTime).toLocaleTimeString()}
          </Text>
        </div>
      </div>

      {/* Main content */}
      <Tabs
        defaultValue="request"
        variant="unstyled"
        className="flex min-h-0 flex-1 flex-col"
      >
        <Tabs.List className="w-full flex-shrink-0 justify-start rounded-none border-b border-neutral-200 bg-transparent p-0 dark:border-neutral-700">
          <Tabs.Tab
            value="request"
            className="rounded-none px-3 py-2 text-xs"
          >
            Request
          </Tabs.Tab>
          <Tabs.Tab
            value="response"
            className="rounded-none px-3 py-2 text-xs"
          >
            Response
          </Tabs.Tab>
          <Tabs.Tab
            value="body"
            className="rounded-none px-3 py-2 text-xs"
          >
            Body
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel
          value="request"
          className="flex-1 overflow-y-auto p-3"
        >
          <div className="space-y-3">
            {/* Request General Info */}
            <div>
              <h3 className="mb-2 text-xs font-semibold">General</h3>
              <div className="space-y-1 text-xs">
                <div className="flex">
                  <span className="w-1/4">Request URL:</span>
                  <code className="truncate">{log.url}</code>
                </div>
                <div className="flex">
                  <span className="w-1/4">Request Method:</span>
                  <span>{log.method}</span>
                </div>
                <div className="flex">
                  <span className="w-1/4">Status Code:</span>
                  <span className="flex items-center gap-1">
                    {!!log.status?.code && (
                      <Badge
                        color={getStatusColor(log.status?.code)}
                        variant="light"
                        size="sm"
                      >
                        {log.status?.code}
                      </Badge>
                    )}
                    {log.status?.text}
                  </span>
                </div>
              </div>
            </div>

            {/* Request Headers Info */}
            <div>
              <h3 className="mb-2 text-xs font-semibold">Request Headers</h3>
              <div className="space-y-1">
                {Object.keys(log.requestHeaders).length > 0 ?
                  Object.entries(log.requestHeaders).map(([name, value], index) => (
                    <div
                      key={index}
                      className="flex text-xs"
                    >
                      <span className="w-1/4 flex-shrink-0">{name}:</span>
                      <code className="flex-1 break-all">{value}</code>
                    </div>
                  ))
                : <Text
                    c="dimmed"
                    size="sm"
                  >
                    No request headers available
                  </Text>
                }
              </div>
            </div>
          </div>
        </Tabs.Panel>

        <Tabs.Panel
          value="response"
          className="flex-1 overflow-y-auto p-3"
        >
          <div className="space-y-1">
            <h3 className="mb-2 text-xs font-semibold">Response Headers</h3>
            {Object.keys(log.responseHeaders).length > 0 ?
              Object.entries(log.responseHeaders).map(([name, value], index) => (
                <div
                  key={index}
                  className="flex text-xs"
                >
                  <span className="w-1/4 flex-shrink-0">{name}:</span>
                  <code className="flex-1 break-all">{value}</code>
                </div>
              ))
            : <Text
                c="dimmed"
                size="sm"
              >
                No response headers available
              </Text>
            }
          </div>
        </Tabs.Panel>

        <Tabs.Panel
          value="body"
          className="relative flex-1 overflow-y-auto p-3"
        >
          {log.responseBody ?
            <>
              <CopyButton value={log.responseBody}>
                {({ copied, copy }) => (
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color={copied ? 'teal' : 'gray'}
                    className="absolute right-1 top-1"
                    onClick={copy}
                  >
                    {copied ?
                      <IconCheck size="0.875rem" />
                    : <IconCopy size="0.875rem" />}
                  </ActionIcon>
                )}
              </CopyButton>
              <pre className="overflow-x-auto text-xs">{log.responseBody}</pre>
            </>
          : <Text
              c="dimmed"
              size="sm"
            >
              No response body available
            </Text>
          }
        </Tabs.Panel>
      </Tabs>
    </div>
  );
}

export function getStatusColor(status?: number): string {
  return match(status)
    .with(undefined, () => 'gray')
    .when(
      (s) => s >= 200 && s < 300,
      () => 'green',
    )
    .when(
      (s) => s >= 300 && s < 400,
      () => 'blue',
    )
    .when(
      (s) => s >= 400 && s < 500,
      () => 'orange',
    )
    .when(
      (s) => s >= 500,
      () => 'red',
    )
    .otherwise(() => 'gray');
}

export function formatSize(size?: number): string {
  return match(size)
    .with(undefined, () => '-')
    .when(
      (s) => s < 1024,
      (s) => `${s}B`,
    )
    .when(
      (s) => s < 1024 * 1024,
      (s) => `${(s / 1024).toFixed(1)}KB`,
    )
    .otherwise((s) => `${(s / (1024 * 1024)).toFixed(1)}MB`);
}

export function formatDuration(duration?: number): string {
  return match(duration)
    .with(undefined, () => '-')
    .when(
      (d) => d < 1000,
      (d) => `${d}ms`,
    )
    .otherwise((d) => `${(d / 1000).toFixed(1)}s`);
}
